import React, { useState, useEffect } from 'react';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import {
  EvenementStats,
  EvenementFilters,
  EvenementList,
  EvenementDetail
} from '../components/evenement';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import api from '../components/api';
import { toast } from 'react-toastify';
import { RefreshCw } from 'lucide-react';
import Swal from 'sweetalert2';

const EvenementPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();
  
  // États pour les données
  const [evenements, setEvenements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedEvenement, setSelectedEvenement] = useState(null);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    search: '',
    periode: '',
    auteur: '',
    ordering: '-date_creation'
  });
  
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Charger les statistiques
  useEffect(() => {
    fetchStats();
  }, []);

  // Charger les événements quand les filtres changent
  useEffect(() => {
    fetchEvenements();
  }, [filters]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/api/evenements/admin/evenements/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchEvenements = async () => {
    try {
      setLoading(true);
      
      // Construire les paramètres de requête
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.periode) params.append('periode', filters.periode);
      if (filters.auteur) params.append('auteur', filters.auteur);
      if (filters.ordering) params.append('ordering', filters.ordering);
      
      const response = await api.get(`/api/evenements/admin/evenements/?${params.toString()}`);
      setEvenements(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error);
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      periode: '',
      auteur: '',
      ordering: '-date_creation'
    });
  };

  const handleViewEvenement = (evenement) => {
    setSelectedEvenement(evenement);
  };

  const handleEditEvenement = (evenement) => {
    setSelectedEvenement(evenement);
  };

  const handleDeleteEvenement = async (evenement) => {
    const result = await Swal.fire({
      title: 'Supprimer cet événement ?',
      text: "Cette action est irréversible ! Tous les participants seront également supprimés.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'rounded-lg px-6 py-2',
        cancelButton: 'rounded-lg px-6 py-2'
      }
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/evenements/admin/evenements/${evenement.id}/`);
        Swal.fire({
          title: 'Supprimé !',
          text: "L'événement a été supprimé avec succès.",
          icon: 'success',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          timer: 2000,
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
        fetchEvenements();
        fetchStats();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        Swal.fire({
          title: 'Erreur !',
          text: "Erreur lors de la suppression de l'événement.",
          icon: 'error',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
      }
    }
  };

  const handleUpdateEvenement = (updatedEvenement) => {
    // Mettre à jour la liste
    setEvenements(prev =>
      prev.map(e => e.id === updatedEvenement.id ? updatedEvenement : e)
    );
    fetchStats();
  };

  const handleRefresh = () => {
    fetchEvenements();
    fetchStats();
    toast.info('Données actualisées');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.periode) count++;
    if (filters.auteur) count++;
    return count;
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={menuItems}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        <main className="flex-1 w-full p-4 lg:p-6 overflow-auto">
          {/* Header avec titre et bouton */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                Gestion des Événements
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                Gérez tous les événements de la plateforme
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55d00] transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>

          {/* Statistiques */}
          {!statsLoading && stats && <EvenementStats stats={stats} />}

          {/* Filtres */}
          <EvenementFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            activeFiltersCount={getActiveFiltersCount()}
          />

          {/* Résultats count */}
          {!loading && evenements.length > 0 && (
            <div className="mb-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{evenements.length}</span> événement{evenements.length > 1 ? 's' : ''} trouvé{evenements.length > 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* Liste des événements */}
          <EvenementList
            evenements={evenements}
            loading={loading}
            onView={handleViewEvenement}
            onEdit={handleEditEvenement}
            onDelete={handleDeleteEvenement}
          />

          {/* Modal détail */}
          {selectedEvenement && (
            <EvenementDetail
              evenement={selectedEvenement}
              onClose={() => setSelectedEvenement(null)}
              onUpdate={handleUpdateEvenement}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default EvenementPage;